import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { 
  Search, 
  UserPlus, 
  Trash2, 
  Ban, 
  Archive, 
  Shield, 
  ShieldCheck, 
  Crown, 
  MoreVertical,
  Loader2,
  CheckCircle,
  XCircle,
  Eye,
  Mail,
  RefreshCw
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { useAdminRole } from "@/hooks/useAdminRole";

interface UserData {
  id: string;
  name: string;
  photo_url: string | null;
  is_verified: boolean;
  is_premium: boolean;
  is_archived: boolean;
  is_disabled: boolean;
  created_at: string;
  last_seen: string | null;
  roles: string[];
  email: string | null;
}

const AdminUserManagementPanel: React.FC = () => {
  const queryClient = useQueryClient();
  const { isAdmin } = useAdminRole();
  const [searchTerm, setSearchTerm] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserData | null>(null);
  const [actionType, setActionType] = useState<string | null>(null);
  const [showAddUserDialog, setShowAddUserDialog] = useState(false);

  // Fetch users
  const { data: users, isLoading, refetch } = useQuery({
    queryKey: ["admin-users", searchTerm, includeArchived],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("admin_get_users", {
        search_term: searchTerm || null,
        include_archived: includeArchived,
        page_num: 1,
        page_size: 50,
      });
      
      if (error) throw error;
      return data as UserData[];
    },
  });

  // Delete user mutation
  const deleteUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      const { error } = await supabase.rpc("admin_delete_user", {
        target_user_id: userId,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("User deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      setSelectedUser(null);
      setActionType(null);
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete user: ${error.message}`);
    },
  });

  // Update user status mutation
  const updateUserMutation = useMutation({
    mutationFn: async ({ userId, field, value }: { userId: string; field: string; value: boolean }) => {
      const { error } = await supabase
        .from("profiles")
        .update({ [field]: value })
        .eq("id", userId);
      if (error) throw error;
    },
    onSuccess: (_, { field, value }) => {
      const action = field === "is_disabled" 
        ? (value ? "disabled" : "enabled")
        : (value ? "archived" : "unarchived");
      toast.success(`User ${action} successfully`);
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      setSelectedUser(null);
      setActionType(null);
    },
    onError: (error: Error) => {
      toast.error(`Failed to update user: ${error.message}`);
    },
  });

  // Manage role mutation
  const manageRoleMutation = useMutation({
    mutationFn: async ({ userId, role, action }: { userId: string; role: "admin" | "moderator" | "verified_user"; action: "add" | "remove" }) => {
      const { error } = await supabase.rpc("admin_manage_user_role", {
        target_user_id: userId,
        new_role: role,
        action: action,
      });
      if (error) throw error;
    },
    onSuccess: (_, { role, action }) => {
      toast.success(`Role ${role} ${action === "add" ? "added" : "removed"} successfully`);
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    },
    onError: (error: Error) => {
      toast.error(`Failed to manage role: ${error.message}`);
    },
  });

  const handleAction = (user: UserData, type: string) => {
    setSelectedUser(user);
    setActionType(type);
  };

  const confirmAction = () => {
    if (!selectedUser || !actionType) return;

    switch (actionType) {
      case "delete":
        deleteUserMutation.mutate(selectedUser.id);
        break;
      case "disable":
        updateUserMutation.mutate({ 
          userId: selectedUser.id, 
          field: "is_disabled", 
          value: !selectedUser.is_disabled 
        });
        break;
      case "archive":
        updateUserMutation.mutate({ 
          userId: selectedUser.id, 
          field: "is_archived", 
          value: !selectedUser.is_archived 
        });
        break;
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case "admin":
        return "bg-red-500/20 text-red-600 dark:text-red-400 border-red-500/30";
      case "moderator":
        return "bg-blue-500/20 text-blue-600 dark:text-blue-400 border-blue-500/30";
      case "verified_user":
        return "bg-green-500/20 text-green-600 dark:text-green-400 border-green-500/30";
      default:
        return "bg-gray-500/20 text-gray-600 dark:text-gray-400 border-gray-500/30";
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Shield className="w-5 h-5 text-primary" />
          User Management
        </h2>
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => refetch()}
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search users by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex items-center gap-2">
          <Switch
            id="include-archived"
            checked={includeArchived}
            onCheckedChange={setIncludeArchived}
          />
          <Label htmlFor="include-archived" className="text-sm text-muted-foreground">
            Include Archived
          </Label>
        </div>
      </div>

      {/* Users List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : users && users.length > 0 ? (
        <div className="space-y-2">
          {users.map((user) => (
            <div
              key={user.id}
              className={cn(
                "flex items-center gap-4 p-4 rounded-xl bg-white/50 dark:bg-gray-800/50 border border-white/60 dark:border-gray-700/60 backdrop-blur-sm",
                user.is_disabled && "opacity-60",
                user.is_archived && "bg-amber-50/50 dark:bg-amber-900/20 border-amber-200/50"
              )}
            >
              {/* Avatar */}
              <Avatar className="h-12 w-12">
                <AvatarImage src={user.photo_url || undefined} alt={user.name} />
                <AvatarFallback className="bg-primary/10 text-primary font-medium">
                  {user.name?.charAt(0)?.toUpperCase() || "?"}
                </AvatarFallback>
              </Avatar>

              {/* User Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium text-foreground truncate">{user.name}</p>
                  {user.is_verified && (
                    <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />
                  )}
                  {user.is_premium && (
                    <Crown className="w-4 h-4 text-amber-500 shrink-0" />
                  )}
                  {user.is_disabled && (
                    <Badge variant="destructive" className="text-xs">Disabled</Badge>
                  )}
                  {user.is_archived && (
                    <Badge variant="secondary" className="text-xs bg-amber-200/50">Archived</Badge>
                  )}
                </div>
                {/* Email */}
                {user.email && (
                  <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                    <Mail className="w-3 h-3" />
                    <span className="truncate">{user.email}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                  <span>Joined {formatDate(user.created_at)}</span>
                  <span>•</span>
                  <span>Last seen {formatDate(user.last_seen)}</span>
                </div>
                {/* Roles */}
                {user.roles && user.roles.length > 0 && (
                  <div className="flex gap-1 mt-2 flex-wrap">
                    {user.roles.map((role) => (
                      <Badge 
                        key={role} 
                        variant="outline" 
                        className={cn("text-xs", getRoleBadgeColor(role))}
                      >
                        {role === "admin" && <Shield className="w-3 h-3 mr-1" />}
                        {role === "moderator" && <ShieldCheck className="w-3 h-3 mr-1" />}
                        {role}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              {/* Actions */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <MoreVertical className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>User Actions</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  
                  {/* Disable/Enable */}
                  <DropdownMenuItem onClick={() => handleAction(user, "disable")}>
                    {user.is_disabled ? (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                        Enable User
                      </>
                    ) : (
                      <>
                        <Ban className="w-4 h-4 mr-2 text-amber-500" />
                        Disable User
                      </>
                    )}
                  </DropdownMenuItem>

                  {/* Archive/Unarchive */}
                  <DropdownMenuItem onClick={() => handleAction(user, "archive")}>
                    <Archive className="w-4 h-4 mr-2" />
                    {user.is_archived ? "Unarchive User" : "Archive User"}
                  </DropdownMenuItem>

                  {isAdmin && (
                    <>
                      <DropdownMenuSeparator />
                      
                      {/* Role Management */}
                      <DropdownMenuSub>
                        <DropdownMenuSubTrigger>
                          <Shield className="w-4 h-4 mr-2" />
                          Manage Roles
                        </DropdownMenuSubTrigger>
                        <DropdownMenuSubContent>
                          {(["admin", "moderator", "verified_user"] as const).map((role) => {
                            const hasRole = user.roles?.includes(role);
                            return (
                              <DropdownMenuItem
                                key={role}
                                onClick={() => manageRoleMutation.mutate({
                                  userId: user.id,
                                  role,
                                  action: hasRole ? "remove" : "add",
                                })}
                              >
                                {hasRole ? (
                                  <XCircle className="w-4 h-4 mr-2 text-destructive" />
                                ) : (
                                  <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                                )}
                                {hasRole ? `Remove ${role}` : `Add ${role}`}
                              </DropdownMenuItem>
                            );
                          })}
                        </DropdownMenuSubContent>
                      </DropdownMenuSub>

                      <DropdownMenuSeparator />
                      
                      {/* Delete */}
                      <DropdownMenuItem 
                        onClick={() => handleAction(user, "delete")}
                        className="text-destructive focus:text-destructive"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete User
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 text-muted-foreground">
          <Search className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No users found</p>
        </div>
      )}

      {/* Confirmation Dialog */}
      <AlertDialog open={!!actionType && !!selectedUser} onOpenChange={() => { setActionType(null); setSelectedUser(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {actionType === "delete" && "Delete User"}
              {actionType === "disable" && (selectedUser?.is_disabled ? "Enable User" : "Disable User")}
              {actionType === "archive" && (selectedUser?.is_archived ? "Unarchive User" : "Archive User")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {actionType === "delete" && (
                <>
                  Are you sure you want to permanently delete <strong>{selectedUser?.name}</strong>? 
                  This action cannot be undone and will remove all associated data.
                </>
              )}
              {actionType === "disable" && (
                <>
                  {selectedUser?.is_disabled 
                    ? `Re-enable ${selectedUser?.name}'s account? They will regain access to the platform.`
                    : `Disable ${selectedUser?.name}'s account? They will not be able to log in.`
                  }
                </>
              )}
              {actionType === "archive" && (
                <>
                  {selectedUser?.is_archived 
                    ? `Unarchive ${selectedUser?.name}'s profile? They will appear in search results again.`
                    : `Archive ${selectedUser?.name}'s profile? They will be hidden from search results but data will be preserved.`
                  }
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmAction}
              className={actionType === "delete" ? "bg-destructive hover:bg-destructive/90" : ""}
            >
              {deleteUserMutation.isPending || updateUserMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminUserManagementPanel;
